import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { classnames } from '@aibees/shared/utils';
import { Box } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { FilterSection } from '../../components/filter-section';
import { useFilterSections } from '../../hooks/use-filter-sections';
import { findOpenFilterItems } from '../../utils';
import { AccordionFilter } from '../accordion-filter';
import { useOpenedItems } from '../checkbox-group-filter/hooks/use-opened-items';
import * as SX from './sx';
import type { AccordionGroupFilterProps as Props } from './types';

export const AccordionGroupFilter = memo(({ className, ...filter }: Props) => {
  const [open, setOpen] = useState<string[]>(findOpenFilterItems(filter));
  const selectKeys = useMemo(
    () => filter.items?.map((i) => i.field!).filter(Boolean) ?? [],
    [filter.items]
  );
  const { params, getParam } = useSearchQueryFilter({ select: selectKeys });
  const { mainItems, moreItems } = useFilterSections(filter);
  const [moreOpened] = useOpenedItems(params, filter, mainItems, moreItems);

  const handleChange = (name: string) => {
    let draft;
    if (!open.includes(name)) draft = [...open, ...[name]];
    else draft = open.filter((x) => x !== name);
    setOpen(draft);
  };

  const isAnyOfMoreItemsSet = useMemo(() => {
    return moreItems?.map((i) => getParam(i.field!)).filter(Boolean);
  }, [moreItems]);

  return (
    <Box
      className={classnames('AccordionGroupFilter', className)}
      display="flex"
      flexDirection="column"
      gap={1}
      sx={SX.root}
      key={filter.name}
    >
      {mainItems?.map(({ key, open: o, ...item }) => (
        <AccordionFilter
          onChange={() => handleChange(item.field!)}
          key={key}
          open={open.includes(item.field!)}
          {...item}
        />
      ))}
      {moreItems?.length ? (
        <FilterSection
          open={!!isAnyOfMoreItemsSet?.length}
          title="More Filters"
        >
          {moreItems?.map(({ key, open: o, ...item }) => (
            <AccordionFilter
              onChange={() => handleChange(item.field!)}
              key={key}
              open={
                open.includes(item.field!) || moreOpened?.includes(item.field!)
              }
              {...item}
            />
          ))}
        </FilterSection>
      ) : null}
    </Box>
  );
});

AccordionGroupFilter.displayName = 'AccordionGroupFilter';
